/**
 * API Key Service — gestão das chaves de API do usuário autenticado.
 *
 * Nenhum método aqui recebe userId: o backend deriva o dono do token da
 * sessão. Chave é credencial, e credencial não se lista por parâmetro de rota.
 */

import { api } from './api';
import {
  ApiKey,
  ApiKeyBackend,
  ApiKeysListResponse,
  CreateApiKeyPayload,
  CreateApiKeyResponse,
  CreatedApiKey,
} from '@/types/apiKey';

const transformApiKey = (backendKey: ApiKeyBackend): ApiKey => ({
  id: backendKey.id,
  name: backendKey.name,
  prefix: backendKey.prefix,
  scopes: backendKey.scopes || [],
  status: backendKey.status,
  lastUsedAt: backendKey.last_used_at,
  expiresAt: backendKey.expires_at,
  revokedAt: backendKey.revoked_at,
  createdAt: backendKey.created_at,
});

export const apiKeyService = {
  /**
   * GET /api-keys
   * Inclui as revogadas: revogação fica registrada, não some da lista.
   */
  getApiKeys: async (): Promise<ApiKey[]> => {
    const response = await api.get<ApiKeysListResponse>('/api-keys');
    return (response?.data || []).map(transformApiKey);
  },

  /**
   * POST /api-keys
   *
   * Devolve o segredo em claro no campo `key`. É a única vez — o backend
   * guarda apenas o hash. Quem chamar precisa exibi-lo imediatamente; não há
   * como buscá-lo de novo depois.
   */
  createApiKey: async (payload: CreateApiKeyPayload): Promise<CreatedApiKey> => {
    const response = await api.post<CreateApiKeyResponse>('/api-keys', payload);
    return { ...transformApiKey(response.data), key: response.data.key };
  },

  /**
   * DELETE /api-keys/:id
   * Revoga (não apaga). A chave para de valer imediatamente.
   */
  revokeApiKey: async (keyId: string): Promise<void> => {
    await api.delete(`/api-keys/${keyId}`);
  },
};

export default apiKeyService;
